// ── ProjectClaudePanel (5C — Contextual Assistant) ───────────────────────────
// Ya no renderiza UI propia — el shell genérico vive en AssistantDock (ver
// contexts/AssistantContext.tsx). Único módulo que usa el mecanismo de
// "pills" derivado de tools (ver chipPlaceholder abajo) — se preserva tal
// cual, el resto de módulos no lo usaba.

import type { AssistantTab } from '../ui/AssistantShell.js';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAssistantModule, useRegisterAssistantActive } from '../../contexts/AssistantContext.js';
import type { ProjectRow, ContentPlan } from '../../hooks/useProjects.js';

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  project: ProjectRow;
  /** @deprecated Content plan updates now happen server-side via tools. Caller may still pass; ignored. */
  plan?: ContentPlan | null;
  /** @deprecated Content plan updates now happen server-side via tools. Caller may still pass; ignored. */
  onPlanChange?: (p: ContentPlan | null) => void;
  section?: string;
  /**
   * Herramientas del módulo (el editor de páginas), en su propia pestaña del
   * dock. Antes iba apilado arriba de la conversación y quedaban las dos cosas
   * amontonadas en la misma columna.
   *
   * (Slot `toolsContent` de
   * AssistantModuleConfig). Hoy: los controles del editor visual.
   */
  toolsContent?: ReactNode;
  /** Pestaña abierta del panel. La controla la página: entrar en Editar abre las herramientas. */
  activeTab?: AssistantTab;
  onActiveTabChange?: (tab: AssistantTab) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ProjectClaudePanel({ project, section, toolsContent, activeTab, onActiveTabChange }: Props) {
  const { t } = useTranslation();

  useAssistantModule('project', String(project.id), section);

  // Entidad de la ApprovalCard: siempre este proyecto — el panel está montado
  // en su detalle, así que el link es autorreferencial (consistente con el
  // pattern; no requiere una llamada extra para resolver un label distinto).
  useRegisterAssistantActive('project', String(project.id), {
    title: t('projects.claude.title'),
    placeholder: t('projects.claude.placeholder', { defaultValue: 'Ask Claude…' }),
    chipPlaceholder: t('projects.claude.assistant.pillPlaceholder', { defaultValue: 'Describí la instrucción…' }),
    emptyHint: t('projects.claude.empty'),
    entityLink: { label: project.name, href: `/proyectos/${project.slug}` },
    ...(toolsContent !== undefined ? { toolsContent } : {}),
    ...(activeTab !== undefined ? { activeTab } : {}),
    ...(onActiveTabChange !== undefined ? { onActiveTabChange } : {}),
  });

  return null;
}
